import styled from 'styled-components';

export const GraphViewport = styled.div`
    position: relative;
    width: 100%;
    min-height: 520px;
    max-height: 72vh;
    overflow: hidden;
    border: 1px solid var(--color-main);
    border-radius: var(--radius-md);
    background:
        radial-gradient(circle at 20% 20%, rgba(68, 168, 128, 0.08), transparent 35%),
        radial-gradient(circle at 80% 0%, rgba(255, 221, 60, 0.06), transparent 30%),
        var(--color-base-secondary);
    cursor: grab;
    user-select: none;

    &:active {
        cursor: grabbing;
    }
`;

export const GraphCanvas = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    transform-origin: 0 0;
    will-change: transform;
`;

export const GraphSvg = styled.svg`
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
    overflow: visible;
`;

export const GraphControls = styled.div`
    position: absolute;
    top: var(--space-3);
    right: var(--space-3);
    z-index: 3;
    display: flex;
    gap: var(--space-2);
`;

export const GraphHint = styled.div`
    position: absolute;
    left: var(--space-3);
    bottom: var(--space-3);
    z-index: 3;
    font-size: var(--font-size-xs);
    color: var(--text-color-secondary);
    background: rgba(19, 20, 32, 0.75);
    padding: 6px 10px;
    border-radius: var(--radius-sm);
`;

export const SearchResults = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    margin-bottom: var(--space-3);
`;

export const SearchResultChip = styled.button`
    display: inline-block;
    padding: 6px 12px;
    border-radius: var(--radius-md);
    background: var(--color-surface-secondary);
    border: 1px solid var(--color-main);
    color: var(--color-white-95);
    font-size: var(--font-size-sm);
    font-weight: 600;
    cursor: pointer;

    &:hover {
        border-color: var(--color-green);
        color: var(--color-white-95);
    }
`;

export const GraphNode = styled.div`
    position: absolute;
    width: ${(props) => props.$width}px;
    min-height: ${(props) => props.$height}px;
    padding: 12px 14px 16px;
    border-radius: 10px;
    border: 1px solid ${(props) => {
        if (props.$selected) {
            return 'var(--color-yellow)';
        }
        return props.$isRoot ? 'var(--color-accent)' : 'var(--color-main)';
    }};
    background: linear-gradient(160deg, var(--color-surface-secondary) 0%, var(--color-surface) 100%);
    box-shadow: ${(props) => (props.$selected
        ? '0 0 0 2px rgba(255, 221, 60, 0.55), 0 10px 24px rgba(0, 0, 0, 0.35)'
        : '0 8px 20px rgba(0, 0, 0, 0.28)')};
    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
    cursor: pointer;
    z-index: 2;

    &:hover {
        border-color: ${(props) => (props.$isRoot ? 'var(--color-yellow-light)' : 'var(--color-green)')};
        transform: translateY(-2px);
    }
`;

export const NodeHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-2);
    margin-bottom: 6px;
`;

export const NodeLevel = styled.span`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: var(--text-color-dark);
    background: ${(props) => (props.$isRoot ? 'var(--color-accent)' : 'var(--color-green)')};
    border-radius: 999px;
    padding: 2px 8px;
`;

export const NodeStatus = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) => (props.$active ? 'var(--color-green-secondary)' : 'var(--color-red)')};
    box-shadow: 0 0 8px ${(props) => (props.$active ? 'rgba(20, 198, 121, 0.6)' : 'rgba(255, 100, 108, 0.5)')};
`;

export const NodeName = styled.div`
    font-size: var(--font-size-md);
    font-weight: 600;
    line-height: 1.3;
    color: var(--color-white);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const NodeMeta = styled.div`
    margin-top: 4px;
    font-size: var(--font-size-xs);
    color: var(--text-color-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const NodeChildrenBadge = styled.button`
    position: absolute;
    left: 50%;
    bottom: -14px;
    transform: translateX(-50%);
    min-width: 28px;
    height: 28px;
    padding: 0 9px;
    border-radius: 14px;
    border: 2px solid ${(props) => (props.$isExpanded ? 'var(--color-green)' : 'var(--color-purple-highlight)')};
    background: ${(props) => (props.$isExpanded ? 'var(--color-green)' : 'var(--color-base)')};
    color: ${(props) => (props.$isExpanded ? 'var(--text-color-dark)' : 'var(--color-white)')};
    font-size: var(--font-size-sm);
    font-weight: 700;
    cursor: pointer;
    z-index: 3;
    line-height: 24px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
    transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;

    &:hover {
        border-color: var(--color-accent);
        background: ${(props) => (props.$isExpanded ? 'var(--color-green-secondary)' : 'var(--color-surface-secondary)')};
        color: var(--color-white);
        transform: translateX(-50%) scale(1.08);
    }

    &:disabled {
        opacity: 0.55;
        cursor: wait;
        transform: translateX(-50%);
    }
`;
